import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusBadge } from '@/components/ui/status-badge';
import type { LegalDocumentAssignmentPublic } from '@pillar-portal/api-types';
import { SYSTEM_DISPLAY_TIME_ZONE, formatTimestampForMerchantUi } from '@pillar-portal/api-types';
import { formatLegalDocumentPurpose } from './legal-document-purpose-format';

interface LegalDocumentAssignmentDetailPageProps {
    readonly assignment: LegalDocumentAssignmentPublic;
}

export function LegalDocumentAssignmentDetailPage({
    assignment,
}: LegalDocumentAssignmentDetailPageProps) {
    return (
        <Card>
            <CardHeader>
                <div className="flex items-start justify-between gap-3">
                    <CardTitle>Assignment details</CardTitle>
                    <StatusBadge status={assignment.status} />
                </div>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
                <div>Assignment: {assignment.legal_document_assignment_uuid}</div>
                <div>
                    Version: {assignment.legal_document.legal_document_title} (
                    {assignment.legal_document.legal_document_version})
                </div>
                <div>Document: {assignment.legal_document.legal_document_uuid}</div>
                <div>
                    Purpose:{' '}
                    {formatLegalDocumentPurpose(assignment.legal_document.legal_document_purpose)}
                </div>
                <div>Digest: {assignment.legal_document.content_sha256}</div>
                <div>Scope: {assignment.scope}</div>
                <div>
                    Organization:{' '}
                    {assignment.organization === null
                        ? 'Default assignment (no Organization)'
                        : `${assignment.organization.organization_name} (${assignment.organization.organization_uuid})`}
                </div>
                <div>
                    Effective from:{' '}
                    {formatTimestampForMerchantUi(
                        assignment.in_force_start,
                        SYSTEM_DISPLAY_TIME_ZONE
                    )}
                </div>
                <div>
                    Effective until:{' '}
                    {assignment.in_force_end === null
                        ? 'No end date'
                        : formatTimestampForMerchantUi(
                              assignment.in_force_end,
                              SYSTEM_DISPLAY_TIME_ZONE
                          )}
                </div>
                <div>Assigned by User: {assignment.assigned_by_user_uuid}</div>
                {assignment.revocation === null ? null : (
                    <div>
                        Revoked:{' '}
                        {formatTimestampForMerchantUi(
                            assignment.revocation.revoked_ts,
                            SYSTEM_DISPLAY_TIME_ZONE
                        )}{' '}
                        by User {assignment.revocation.revoked_by_user_uuid}
                        {assignment.revocation.revoke_reason === null
                            ? ''
                            : ` (${assignment.revocation.revoke_reason})`}
                    </div>
                )}
                <div>
                    Supersession:{' '}
                    {assignment.scope === 'ORGANIZATION' &&
                    assignment.legal_document.legal_document_purpose === 'USER_AGREEMENT'
                        ? 'While effective, the platform default user agreements no longer apply to this Organization or its sub-Organizations, and it replaces any inherited user agreement with the same title.'
                        : 'Does not replace other agreements.'}
                </div>
            </CardContent>
        </Card>
    );
}
